// "What can stay with the homeowner" (Oct 30, 2026): tick the carrier's scope
// lines Bardin won't do. Their ACV comes off the homeowner's deductible (the
// credits stop at the deductible), their recoverable depreciation stays with
// the carrier. Greg (Oct 29): credits come only from ticked carrier lines.
//
// The picker reads the lines of one read (scope_extractions) and hands the
// picks back through onSave; the caller writes them (the scope check card
// straight to the deal, the scope editor into its own save). Trigger
// deals_apply_scope_picks turns picks into acv_credit / dep_credit.

import { useEffect, useMemo, useState } from "react";
import type { Deal } from "../../lib/types";
import { fetchReadLines, fmtScopeMoney as money } from "../../lib/scopeGate";
import {
  groupLines,
  lineAcv,
  lineDep,
  lineMeta,
  matchPicks,
  pickTotals,
  shareWithPicks,
  toPick,
  type PickedLine,
  type ScopeLine,
} from "../../lib/scopeLines";

export default function ScopeLinePicker({
  deal,
  readId,
  initialPicks,
  onClose,
  onSave,
  saveLabel = "Save",
}: {
  /** The deal as the math should see it (the scope editor passes its unsaved numbers). */
  deal: Partial<Deal>;
  readId: string;
  initialPicks: PickedLine[];
  onClose: () => void;
  /** Writes the picks; throw to keep the picker open with the error. */
  onSave: (picks: PickedLine[]) => void | Promise<void>;
  saveLabel?: string;
}) {
  const [lines, setLines] = useState<ScopeLine[] | null>(null);
  const [mainCoverage, setMainCoverage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [stale, setStale] = useState<PickedLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchReadLines(readId);
        if (cancelled) return;
        setLines(r.lines);
        setMainCoverage(r.mainCoverage);
        const m = matchPicks(r.lines, initialPicks);
        setSelected(m.selected);
        setStale(m.stale);
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message ?? "Couldn't load the scope's lines.");
      }
    })();
    return () => { cancelled = true; };
    // initialPicks is read once, when the picker opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readId]);

  const grouped = useMemo(() => (lines ? groupLines(lines, mainCoverage) : null), [lines, mainCoverage]);
  const picks = useMemo(
    () => (lines ? [...selected].sort((a, b) => a - b).map((i) => toPick(lines[i], readId)) : []),
    [lines, selected, readId],
  );
  const share = shareWithPicks(deal, picks);
  const totals = pickTotals(picks);
  const typedCredits =
    initialPicks.length === 0 && ((Number(deal.acv_credit) || 0) > 0.004 || (Number(deal.dep_credit) || 0) > 0.004);

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  async function save(list: PickedLine[]) {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(list);
      onClose();
    } catch (e) {
      setSaveError((e as { message?: string })?.message ?? "Couldn't save the lines.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-slate-50 w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500">Carrier's scope</div>
            <div className="text-base font-semibold text-bardin-ink">What can stay with the homeowner</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Tick the lines Bardin won't do. Their ACV comes off the homeowner's deductible; their depreciation stays with the carrier until the work is done.
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 -mr-2 text-slate-500 hover:text-bardin-ink rounded-lg hover:bg-slate-100 disabled:opacity-40"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
          {loadError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">{loadError}</div>
          )}
          {!lines && !loadError && <div className="text-sm text-slate-500">Loading the scope's lines…</div>}

          {typedCredits && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              This deal has credits typed in (ACV {money(Number(deal.acv_credit) || 0)}, depreciation {money(Number(deal.dep_credit) || 0)}).
              Saving replaces them with the lines ticked here.
            </div>
          )}
          {stale.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <div className="font-semibold">
                {stale.length === 1 ? "1 line picked earlier isn't" : `${stale.length} lines picked earlier aren't`} on this scope any more
              </div>
              <ul className="mt-1 list-disc pl-4">
                {stale.map((p, i) => (
                  <li key={i}>{p.n ? `#${p.n} ` : ""}{p.description} · ACV {money(p.acv)}</li>
                ))}
              </ul>
              <div className="mt-1">Saving keeps only the lines ticked here.</div>
            </div>
          )}

          {grouped && grouped.groups.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              The scope has no line items on its main coverage to pick from.
            </div>
          )}

          {grouped?.groups.map((g) => {
            const ticked = g.items.filter((it) => selected.has(it.index)).length;
            const body = (
              <ul className="divide-y divide-slate-100">
                {g.items.map(({ line, index }) => {
                  const on = selected.has(index);
                  const dep = lineDep(line);
                  return (
                    <li key={index}>
                      <label className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer ${on ? "bg-amber-50" : "hover:bg-slate-50"}`}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(index)}
                          disabled={saving}
                          className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-amber-600"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-bardin-ink">{line.description}</span>
                          <span className="block text-[11px] text-slate-500">
                            {[line.n ? `#${line.n}` : null, lineMeta(line) || null].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        <span className="shrink-0 text-right">
                          <span className="block text-sm font-semibold text-bardin-ink">{money(lineAcv(line))}</span>
                          <span className="block text-[10px] text-slate-500">
                            ACV{dep > 0 ? ` · dep ${money(dep)}${line.depreciation_non_recoverable ? " (not recoverable)" : ""}` : ""}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            );
            const head = (
              <div className="flex items-baseline justify-between gap-2">
                <span>
                  <span className="text-sm font-semibold text-bardin-ink">{g.title}</span>
                  <span className="ml-2 text-[11px] text-slate-500">{g.hint}</span>
                </span>
                <span className="text-[11px] text-slate-500 shrink-0">
                  {ticked > 0 ? `${ticked} of ${g.items.length} ticked` : `${g.items.length} line${g.items.length === 1 ? "" : "s"}`}
                </span>
              </div>
            );
            return g.folded ? (
              <details key={g.key} open={ticked > 0} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <summary className="cursor-pointer select-none px-3 py-2.5 bg-slate-50">{head}</summary>
                {body}
              </details>
            ) : (
              <div key={g.key} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-3 py-2.5 bg-slate-50 border-b border-slate-100">{head}</div>
                {body}
              </div>
            );
          })}

          {grouped && grouped.otherCoverage > 0 && (
            <div className="text-[11px] text-slate-500">
              {grouped.otherCoverage === 1 ? "1 more line is" : `${grouped.otherCoverage} more lines are`} on other coverages
              (code upgrade, other structures, contents). They aren't in this deal's numbers, so they can't be left here.
            </div>
          )}
        </div>

        {/* The homeowner's share, live */}
        <div className="bg-white border-t border-slate-200 px-4 sm:px-6 py-3">
          <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 text-xs">
            <span className="text-slate-600">Deductible</span>
            <span className="text-right text-bardin-ink">{money(share.deductible)}</span>
            {share.addOns > 0 && (
              <>
                <span className="text-slate-600">+ Add-ons the homeowner pays</span>
                <span className="text-right text-bardin-ink">{money(share.addOns)}</span>
              </>
            )}
            <span className="text-slate-600">
              − Work Bardin won't do (ACV{totals.count > 0 ? `, ${totals.count} line${totals.count === 1 ? "" : "s"}` : ""})
            </span>
            <span className="text-right text-emerald-700">−{money(share.acvApplied)}</span>
            {share.otherApplied > 0 && (
              <>
                <span className="text-slate-600">− Other credits</span>
                <span className="text-right text-emerald-700">−{money(share.otherApplied)}</span>
              </>
            )}
            <span className="font-semibold text-bardin-ink border-t border-slate-200 pt-1 mt-0.5">Homeowner pays Bardin before the build</span>
            <span className="text-right font-bold text-bardin-ink border-t border-slate-200 pt-1 mt-0.5">{money(share.homeownerPays)}</span>
          </div>
          {share.dep > 0 && (
            <div className="mt-1 text-[11px] text-slate-500">
              The carrier keeps {money(share.dep)} of depreciation on these lines until the work is done.
            </div>
          )}
          {share.overage > 0 && (
            <div className="mt-1.5 rounded bg-rose-50 border border-rose-200 px-2 py-1 text-[11px] text-rose-800">
              Credits stop at the deductible: {money(share.overage)} of credit isn't collected.
            </div>
          )}
          {saveError && (
            <div className="mt-1.5 rounded bg-rose-50 border border-rose-200 px-2 py-1 text-xs text-rose-800">{saveError}</div>
          )}
          <div className="mt-2 flex items-center justify-end gap-2 flex-wrap">
            <button onClick={onClose} disabled={saving} className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
              Cancel
            </button>
            {(selected.size > 0 || initialPicks.length > 0 || typedCredits) && (
              <button
                onClick={() => {
                  if (!window.confirm("Clear every line left with the homeowner? The deal then has no credit for work Bardin won't do.")) return;
                  void save([]);
                }}
                disabled={saving || !lines}
                className="px-3 py-2 text-sm font-medium bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                Clear all
              </button>
            )}
            <button
              onClick={() => void save(picks)}
              disabled={saving || !lines}
              className="px-4 py-2 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : `${saveLabel}${picks.length > 0 ? ` (${picks.length} line${picks.length === 1 ? "" : "s"})` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
